/**
 * Secret Santa Form Component
 * Form for creating/editing Secret Santa events
 */

import { useState } from 'react'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Textarea } from '@/components/common/Textarea'
import { SecretSantaFormData } from '@/services/secretSanta.service'

interface SecretSantaFormProps {
  initialData?: Partial<SecretSantaFormData>
  onSubmit: (data: SecretSantaFormData) => Promise<void>
  onCancel: () => void
  submitLabel?: string
}

export const SecretSantaForm = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Create Event',
}: SecretSantaFormProps) => {
  const [formData, setFormData] = useState<SecretSantaFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    eventDate: initialData?.eventDate || null,
    budget: initialData?.budget || null,
    currency: initialData?.currency || 'SGD',
    allowSelfDraw: initialData?.allowSelfDraw || false,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    if (formData.title.length > 100) {
      setError('Title must be 100 characters or less')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Title */}
      <Input
        id="title"
        label="Event Title"
        type="text"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="e.g., Family Christmas 2025"
        maxLength={100}
        required
      />

      {/* Description */}
      <Textarea
        id="description"
        label="Description"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        rows={3}
        placeholder="Add any details or rules for participants..."
      />

      {/* Event Date */}
      <Input
        id="eventDate"
        label="Event Date (Optional)"
        type="date"
        value={formData.eventDate ? formData.eventDate.toISOString().split('T')[0] : ''}
        onChange={(e) =>
          setFormData({
            ...formData,
            eventDate: e.target.value ? new Date(e.target.value) : null,
          })
        }
      />

      {/* Budget */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            id="budget"
            label="Budget (Optional)"
            type="number"
            value={formData.budget ? formData.budget / 100 : ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                budget: e.target.value ? parseFloat(e.target.value) * 100 : null,
              })
            }
            placeholder="0.00"
            step="0.01"
            min="0"
            leftIcon={<span className="text-gray-500">$</span>}
          />
        </div>

        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
            Currency
          </label>
          <select
            id="currency"
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="SGD">SGD (S$)</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
          </select>
        </div>
      </div>

      {/* Allow Self Draw */}
      <div className="flex items-start">
        <input
          type="checkbox"
          id="allowSelfDraw"
          checked={formData.allowSelfDraw}
          onChange={(e) => setFormData({ ...formData, allowSelfDraw: e.target.checked })}
          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="allowSelfDraw" className="ml-2 block text-sm text-gray-700">
          <span className="font-medium">Allow self-draw</span>
          <p className="text-gray-500 mt-0.5">
            Allow participants to draw their own name (not recommended)
          </p>
        </label>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : submitLabel}
        </Button>
      </div>
    </form>
  )
}
